import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PixelCanvas } from '../components/PixelCanvas';
import { ColorPicker } from '../components/ColorPicker';
import { ArrowLeft, ZoomIn, ZoomOut, Hand, Trash2, Copy, Check, Palette } from 'lucide-react';
import { toast } from 'sonner';
import type { Canvas, RecipePixel } from '../types';

export default function CreatePage() {
  const [canvas, setCanvas] = useState<Canvas | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ shareUrl: string; pixelCount: number; cost: number } | null>(null);
  const [copied, setCopied] = useState(false);

  // Canvas navigation
  const [zoom, setZoom] = useState(6);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Pixel selection
  const [selectedPixels, setSelectedPixels] = useState<Map<string, RecipePixel>>(new Map());
  const [currentColor, setCurrentColor] = useState('#FF0000');
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    fetch(`/api/canvas`)
      .then((res) => res.json())
      .then((data: Canvas) => setCanvas(data))
      .catch((err) => console.error('Failed to load canvas:', err));
  }, []);

  // Center canvas on load
  useEffect(() => {
    if (containerRef.current && canvas) {
      const container = containerRef.current;
      const canvasSize = 100 * zoom;
      setOffset({
        x: (container.clientWidth - canvasSize) / 2,
        y: (container.clientHeight - canvasSize) / 2
      });
    }
  }, [canvas, zoom]);

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 1 : -1;
      setZoom(prev => Math.max(3, Math.min(12, prev + delta)));
    }
  };

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return null;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setLastTouchDistance(getTouchDistance(e.touches));
      setIsPanning(false);
    } else if (e.touches.length === 1 && e.shiftKey) {
      setIsPanning(true);
      setPanStart({
        x: e.touches[0].clientX - offset.x,
        y: e.touches[0].clientY - offset.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistance) {
      e.preventDefault();
      const newDistance = getTouchDistance(e.touches);
      if (newDistance) {
        const scale = newDistance / lastTouchDistance;
        setZoom(prev => Math.max(3, Math.min(12, prev * scale)));
        setLastTouchDistance(newDistance);
      }
    } else if (e.touches.length === 1 && isPanning) {
      setOffset({
        x: e.touches[0].clientX - panStart.x,
        y: e.touches[0].clientY - panStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
    setLastTouchDistance(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0 && e.shiftKey) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handlePixelClick = (x: number, y: number) => {
    if (isPanning) return;

    const key = `${x},${y}`;
    const newPixels = new Map(selectedPixels);

    if (newPixels.has(key)) {
      newPixels.delete(key);
    } else {
      newPixels.set(key, { x, y, color: currentColor });
    }

    setSelectedPixels(newPixels);
  };

  const handleClear = () => {
    setSelectedPixels(new Map());
  };

  const handleSubmit = async () => {
    if (selectedPixels.size === 0) {
      toast.error('Keine Pixel ausgewählt', {
        description: 'Bitte wähle mindestens ein Pixel aus, um ein Rezept zu erstellen.'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/recipes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pixels: Array.from(selectedPixels.values()) }),
      });

      if (!response.ok) {
        throw new Error('Failed to create recipe');
      }

      const data: { shareUrl: string; pixelCount: number; cost: number } = await response.json();
      setResult(data);
      toast.success('Rezept erstellt!', {
        description: 'Dein Rezept-Link ist bereit zum Teilen.'
      });
    } catch (error) {
      console.error('Error creating recipe:', error);
      toast.error('Fehler beim Erstellen', {
        description: 'Bitte versuche es erneut.'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(result.shareUrl);
      setCopied(true);
      toast.success('Link kopiert!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetView = () => {
    if (containerRef.current && canvas) {
      const container = containerRef.current;
      const canvasSize = 100 * zoom;
      setOffset({
        x: (container.clientWidth - canvasSize) / 2,
        y: (container.clientHeight - canvasSize) / 2
      });
    }
  };

  const pixelCount = selectedPixels.size;
  const cost = Math.ceil(pixelCount / 3);

  if (result) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full space-y-6">
          <div className="rounded-lg border border-green-500/50 bg-green-500/10 p-6">
            <h2 className="text-2xl font-semibold text-green-500 mb-2">
              Rezept erstellt!
            </h2>
            <p className="text-muted-foreground">
              Dein Rezept wurde erstellt. Teile diesen Link beim Spenden über StreamElements.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-neutral-800 p-6">
            <label className="text-sm font-medium mb-2 block">Link teilen</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={result.shareUrl}
                readOnly
                className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-mono"
              />
              <button
                onClick={copyToClipboard}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Kopiert!' : 'Kopieren'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-border bg-neutral-800 p-4">
              <p className="text-sm text-muted-foreground">Pixel</p>
              <p className="text-3xl font-bold mt-1">{result.pixelCount}</p>
            </div>
            <div className="rounded-lg border border-border bg-neutral-800 p-4">
              <p className="text-sm text-muted-foreground">Kosten</p>
              <p className="text-3xl font-bold mt-1">{result.cost}€</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/"
              className="flex-1 inline-flex items-center justify-center rounded-md bg-secondary px-4 py-3 text-sm font-medium hover:bg-secondary/80 transition-colors"
            >
              Canvas anzeigen
            </Link>
            <Link
              to="/create"
              className="flex-1 inline-flex items-center justify-center rounded-md border border-input px-4 py-3 text-sm font-medium hover:bg-accent transition-colors"
            >
              Weiteres Rezept erstellen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-neutral-900">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-10 border-b border-border bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80">
        <div className="flex items-center justify-between px-3 md:px-4 h-14">
          <div className="flex items-center gap-2 md:gap-4">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md p-2 hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-base md:text-lg font-semibold">Rezept erstellen</h1>
            <div className="text-xs md:text-sm text-muted-foreground">
              {pixelCount} Pixel • {cost}€
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Mobile Color Picker Toggle */}
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="md:hidden inline-flex items-center justify-center rounded-md border border-input px-3 py-2 text-sm font-medium hover:bg-accent transition-colors"
              title="Farbe wählen"
            >
              <Palette className="w-4 h-4" />
            </button>

            <button
              onClick={handleClear}
              disabled={pixelCount === 0}
              className="hidden sm:inline-flex items-center justify-center gap-2 rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden md:inline">Löschen</span>
            </button>
            <button
              onClick={handleClear}
              disabled={pixelCount === 0}
              className="sm:hidden inline-flex items-center justify-center rounded-md border border-input p-2 text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Löschen"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleSubmit}
              disabled={pixelCount === 0 || loading}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-3 md:px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Erstelle...' : <span className="hidden sm:inline">Link generieren</span>}
              {loading ? '' : <span className="sm:hidden">Erstellen</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className={`absolute inset-0 pt-14 ${isPanning ? 'cursor-grabbing' : 'cursor-crosshair'}`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            position: 'absolute',
            left: 0,
            top: 0,
          }}
        >
          <PixelCanvas
            canvas={canvas}
            pixelSize={zoom}
            highlightPixels={Array.from(selectedPixels.values())}
            onPixelClick={handlePixelClick}
            canvasOpacity={0.4}
          />
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-2 md:px-3 py-2 shadow-lg">
          <button
            onClick={() => setZoom(prev => Math.max(3, prev - 1))}
            disabled={zoom <= 3}
            className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Rauszoomen"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          <span className="text-sm text-muted-foreground min-w-[50px] md:min-w-[60px] text-center">
            {zoom}x
          </span>

          <button
            onClick={() => setZoom(prev => Math.min(12, prev + 1))}
            disabled={zoom >= 12}
            className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Reinzoomen"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-border mx-1" />

          <button
            onClick={resetView}
            className="inline-flex items-center justify-center rounded-md px-2 md:px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
            title="Ansicht zurücksetzen"
          >
            <Hand className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Zurücksetzen</span>
          </button>
        </div>
      </div>

      {/* Color Picker Panel - Desktop: right side, Mobile: bottom sheet */}
      <div className={`absolute z-10 transition-transform duration-300 ${showColorPicker ? 'translate-y-0' : 'translate-y-full'
        } md:translate-y-0 bottom-0 left-0 right-0 md:right-4 md:top-20 md:left-auto md:bottom-auto md:w-80`}>
        <div className="rounded-t-lg md:rounded-lg border-t md:border border-border bg-neutral-800/98 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/95 p-4 shadow-lg space-y-4 max-h-[70vh] md:max-h-none overflow-y-auto">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Farbauswahl</h3>
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors md:hidden"
            >
              Schließen
            </button>
          </div>

          <ColorPicker selectedColor={currentColor} onColorChange={setCurrentColor} />

          {/* Mobile: Show selected color prominently */}
          <div className="md:hidden pt-2 border-t border-border">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded border-2 border-border"
                style={{ backgroundColor: currentColor }}
              />
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Gewählte Farbe</p>
                <p className="font-mono text-sm font-semibold">{currentColor}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Help Text - Hidden on mobile */}
      <div className="hidden md:block absolute bottom-4 right-4 z-10 text-xs text-muted-foreground bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-3 py-2 rounded-md border border-border">
        <div>Klicken zum Auswählen • Shift + Ziehen zum Schwenken • Strg/Cmd + Scrollen zum Zoomen</div>
      </div>

      {/* Mobile Help */}
      <div className="md:hidden absolute top-20 left-4 right-4 z-10 text-xs text-center text-muted-foreground bg-neutral-800/95 backdrop-blur supports-[backdrop-filter]:bg-neutral-800/80 px-3 py-2 rounded-md border border-border">
        Tippen zum Auswählen • Pinch zum Zoomen
      </div>
    </div>
  );
}
